import type { Shape } from './shapes';
import { NATURAL_H, NATURAL_W } from './render';
import { loadBugSprite } from './bugSpriteImage';
import { drawAnnotated } from './draw';

// Flatten a view (image + shapes) into a PNG data URL. The uploaded
// raster is what board cards render and what Claude Code sees via the
// MCP `get_capture_asset` tool. This is the "composite a pin/annotation
// onto a bitmap" primitive shared by the web canvas, the widget, the
// extension overlay, and the v2 timeline.

export async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // crossOrigin='anonymous' is benign on same-origin URLs (the
    // browser ignores CORS for those) and required if storage URLs
    // ever go back to a cross-origin host. As of the proxy-reads
    // migration (docs/deployment-DO/STORAGE_PROXY_READS_PLAN.md) capture URLs are
    // SAME-origin (`app.toado.dev/api/v1/storage/...`) so canvas
    // taint isn't a concern in current deployments. Keeping the
    // attribute set means a future flip back to direct-from-CDN
    // won't re-introduce the SecurityError class of bug — the
    // canvas-taint check requires ACAO from any future cross-origin
    // storage host. For data: URLs (uploaded images via FileReader)
    // the attribute is harmless — the browser ignores crossOrigin on
    // data sources.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/**
 * Rasterize `shapes` over an image into a NATURAL_W x NATURAL_H PNG
 * data URL. Returns null when there is nothing to draw (no image and no
 * shapes), when no 2D context is available, or when the canvas is
 * CORS-tainted so `toDataURL` throws.
 *
 * `imageUrl` (an explicit carousel image) wins over `screenshotUrl`
 * (the ticket's original capture); pass exactly the shapes scoped to
 * that image — this function does no imageId filtering.
 */
export async function rasterizeAnnotatedImage(args: {
  shapes: Shape[];
  imageUrl?: string;
  screenshotUrl?: string;
}): Promise<string | null> {
  const { shapes, imageUrl, screenshotUrl } = args;
  const hasShapes = shapes.length > 0;
  const primaryImg = imageUrl || screenshotUrl;
  if (!hasShapes && !primaryImg) return null;

  const canvas = document.createElement('canvas');
  canvas.width = NATURAL_W;
  canvas.height = NATURAL_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Leave the canvas transparent. That way the rasterized PNG's letterbox
  // area (visible when the source image isn't square) lets whatever card /
  // surface it renders on show through — light card stays white, dark card
  // stays dark. Baking a hard white in here forced a bright square on dark
  // theme cards. `canvas` is already zero-alpha by default, so this is just
  // a marker to prevent anyone from adding a fill back in "to be safe".

  let image: { source: CanvasImageSource; width: number; height: number } | null = null;
  if (primaryImg) {
    try {
      const img = await loadImage(primaryImg);
      image = { source: img, width: img.naturalWidth, height: img.naturalHeight };
    } catch {
      // Image load failed (404, CORS-taint, etc). Fall through — shapes
      // still get drawn over the transparent background.
    }
  }

  // A flatten is a one-shot export, so it waits for the bug sprite rather
  // than falling back the way a live canvas does. A canvas that draws the
  // generic bug repaints when the sprite lands; a PNG that is uploaded and
  // shown on a ticket does not, so the fallback would be permanent and the
  // ticket would disagree with the page it was captured from.
  if (shapes.some((s) => s.kind === 'pin' && s.style === 'bug')) await loadBugSprite();

  drawAnnotated(ctx, image, shapes);

  // If the canvas is tainted (cross-origin image without ACAO),
  // toDataURL throws.
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
